/**
 * Zustand store for frame management
 * Handles frame selection and mock frame generation
 */

import { create } from 'zustand';
import { Frame, FrameType, ControlCodes } from '../types/protocol';
import { ParsedFrame, parseFrame } from '../utils/frameParser';
import { buildDataFrame } from '../utils/frameBuilder';

interface FrameStore {
  // Frame list
  frames: Frame[];
  addFrame: (frame: Frame) => void;
  clearFrames: () => void;

  // Selected frame
  selectedFrame: Frame | null;
  parsedFrame: ParsedFrame | null;
  selectFrame: (frame: Frame | null) => void;

  // Mock frame generation
  generateMockFrames: () => void;
}

// Helper to create a mock frame
function createMockFrame(
  type: FrameType,
  raw: Uint8Array,
  direction: 'tx' | 'rx',
  timestampOffset: number = 0
): Frame {
  return {
    type,
    raw,
    decoded: {
      data: raw,
      crc: 0,
      crcValid: true
    },
    timestamp: Date.now() - timestampOffset,
    direction
  };
}

// Generate realistic mock frames
function generateRealisticMockFrames(): Frame[] {
  const frames: Frame[] = [];
  let time = 10000;

  // 1. Test de comunicaciones TOK
  frames.push(createMockFrame(
    FrameType.TEST_COMMUNICATION,
    new Uint8Array([ControlCodes.TOK]),
    'tx',
    time
  ));
  time -= 50;

  // 2. Respuesta TRT
  frames.push(createMockFrame(
    FrameType.TEST_COMMUNICATION,
    new Uint8Array([ControlCodes.TRT]),
    'rx',
    time
  ));
  time -= 500;

  // 3. Consulta de hora (0xBF 0x46) - HOST a REGULADOR
  // Header: canal dest=1 (ICOM), canal orig=1, dest=H0-C1-R1-S0, orig=H0-C0-R0-S0, contador=1
  const consultaHora = new Uint8Array([
    0x21,       // Canal dest=1, bit5=1 (espera resp), no es respuesta
    0x21,       // Canal orig=1, espera respuesta
    0x01, 0x01, // Destino físico: H0-C1-R1-S0
    0x00, 0x00, // Origen físico: H0-C0-R0-S0 (HOST)
    0x01,       // Contador
    0xBF, 0x46  // Comando: Consulta hora
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(consultaHora),
    'tx',
    time
  ));
  time -= 100;

  // 4. Respuesta hora
  const respuestaHora = new Uint8Array([
    0x81,       // Respuesta, canal dest=1
    0x01,       // Canal orig=1
    0x00, 0x00, // Destino: HOST
    0x01, 0x01, // Origen: Regulador
    0x01,       // Contador (mismo que orden)
    // Datos: fecha (días julianos) + hora (décimas)
    0x41, 0x2D, // Fecha: 16685 días desde 1980 = ~2025
    0x00, 0x4B, 0xA3, 0x70  // Hora: ~540000 décimas = 15:00:00
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(respuestaHora),
    'rx',
    time
  ));
  time -= 1000;

  // 5. Test TML (error anterior)
  frames.push(createMockFrame(
    FrameType.TEST_COMMUNICATION,
    new Uint8Array([ControlCodes.TML]),
    'tx',
    time
  ));
  time -= 50;

  // 6. TRT respuesta
  frames.push(createMockFrame(
    FrameType.TEST_COMMUNICATION,
    new Uint8Array([ControlCodes.TRT]),
    'rx',
    time
  ));
  time -= 500;

  // 7. Petición plan en curso (0x85) - orden simple
  const planEnCurso = new Uint8Array([
    0x21,       // Canal dest=1, espera respuesta
    0x21,       // Canal orig=1
    0x01, 0x01, // Destino: Regulador H0-C1-R1
    0x00, 0x00, // Origen: HOST
    0x02,       // Contador=2
    0x85        // Comando: Plan en curso
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(planEnCurso),
    'tx',
    time
  ));
  time -= 150;

  // 8. Respuesta plan en curso
  const respuestaPlan = new Uint8Array([
    0x81,       // Respuesta
    0x01,       // Canal orig=1
    0x00, 0x00, // Destino: HOST
    0x01, 0x01, // Origen: Regulador
    0x02,       // Contador
    // Datos del plan
    0x00,       // Plan 0 (externo)
    0x00, 0x01, 0x5F, 0x90, // Referencia: 90000 décimas
    0x31, 0x18, // Índices: estructura=3, trans=1, tiempos=1, coordinado=1
    0x00, 0x96, // Desfase: 150 décimas = 15s
    0x03, 0x84, // Ciclo: 900 décimas = 90s
    0x01, 0x2C, // Fase A: 300 décimas = 30s
    0x00, 0x32, // Fase B: 50 décimas = 5s (ámbar)
    0x01, 0x2C, // Fase C: 300 décimas = 30s
    0x00, 0x32  // Fase D: 50 décimas = 5s (ámbar)
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(respuestaPlan),
    'rx',
    time
  ));
  time -= 2000;

  // 9. Selección de plan (0x84)
  const seleccionPlan = new Uint8Array([
    0x01,       // Canal dest=1, no espera respuesta para este caso
    0x01,       // Canal orig=1
    0x01, 0x01, // Destino: Regulador
    0x00, 0x00, // Origen: HOST
    0x03,       // Contador=3
    0x84,       // Comando: Selección plan
    0x06,       // Estado: 6 = Centralizado
    0x03,       // Plan: 3
    0xFF, 0xFF, 0xFF // Referencia: -1 (usar última)
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(seleccionPlan),
    'tx',
    time
  ));
  time -= 100;

  // 10. Control de flujo ACK
  frames.push(createMockFrame(
    FrameType.FLOW_CONTROL,
    new Uint8Array([ControlCodes.ESC, ControlCodes.ACK0]),
    'rx',
    time
  ));
  time -= 1000;

  // 11. Notificación cambios de estado (0xC5)
  const notifEstado = new Uint8Array([
    0x21,       // Canal dest=1
    0x21,       // Canal orig=1, espera respuesta
    0x01, 0x01, // Destino: Regulador
    0x00, 0x00, // Origen: HOST
    0x04,       // Contador=4
    0xC5        // Comando: Notif cambios estado
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(notifEstado),
    'tx',
    time
  ));
  time -= 120;

  // 12. Respuesta con estado actual
  const respuestaEstado = new Uint8Array([
    0x81,       // Respuesta
    0x01,       // Canal orig=1
    0x00, 0x00, // Destino: HOST
    0x01, 0x01, // Origen: Regulador
    0x04,       // Contador
    // Estado actual
    0x06,       // Estado: 6 = Centralizado
    0x45,       // Origen: 'E' = Externo
    0x41, 0x2D, // Fecha del cambio
    0x00, 0x4B, 0xA0 // Hora del cambio
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(respuestaEstado),
    'rx',
    time
  ));
  time -= 3000;

  // 13. Petición detectores tiempo real (0xC4)
  const notifDetectores = new Uint8Array([
    0x21,       // Canal dest=1
    0x21,       // Canal orig=1
    0x01, 0x01, // Destino: Regulador
    0x00, 0x00, // Origen: HOST
    0x05,       // Contador=5
    0xC4,       // Comando
    0x00,       // Tipo: 0 = físicos
    0x00, 0x00, 0x00, 0x0F // Máscara: detectores 1-4
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(notifDetectores),
    'tx',
    time
  ));
  time -= 80;

  // 14. Respuesta detectores
  const respuestaDetectores = new Uint8Array([
    0x81,       // Respuesta
    0x01,       // Canal
    0x00, 0x00, // Destino
    0x01, 0x01, // Origen
    0x05,       // Contador
    0x00,       // Sin error
    0x00, 0x00, 0x00, 0x05 // Estado: detectores 1 y 3 activos
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(respuestaDetectores),
    'rx',
    time
  ));
  time -= 500;

  // 15. Test de equipos (0xD4 0x45)
  const testEquipos = new Uint8Array([
    0x21,
    0x21,
    0x01, 0x01,
    0x00, 0x00,
    0x06,
    0xD4, 0x45
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(testEquipos),
    'tx',
    time
  ));
  time -= 100;

  // 16. Respuesta test equipos
  const respuestaTestEq = new Uint8Array([
    0x81,
    0x01,
    0x00, 0x00,
    0x01, 0x01,
    0x06,
    0x0F  // Máscara: alarmas, detectores, posición, estado habilitados
  ]);
  frames.push(createMockFrame(
    FrameType.INFORMATION,
    buildDataFrame(respuestaTestEq),
    'rx',
    time
  ));

  return frames;
}

export const useFrameStore = create<FrameStore>((set, get) => ({
  frames: [],
  selectedFrame: null,
  parsedFrame: null,

  addFrame: (frame) => set((state) => ({
    frames: [...state.frames, frame].slice(-100) // Keep last 100 frames
  })),

  clearFrames: () => set({ frames: [], selectedFrame: null, parsedFrame: null }),

  selectFrame: (frame) => {
    if (frame) {
      const parsed = parseFrame(frame.raw);
      set({ selectedFrame: frame, parsedFrame: parsed });
    } else {
      set({ selectedFrame: null, parsedFrame: null });
    }
  },

  generateMockFrames: () => {
    const mockFrames = generateRealisticMockFrames();
    set({ frames: mockFrames, selectedFrame: null, parsedFrame: null });
  }
}));
